import type { DoerProfilePatch, DoerProfileView } from '@doneverse/shared'
import { api, isNotFoundError } from '../api/index.js'
import { uploadDoerIntroVideo } from './uploadService.js'

/**
 * The doer's founder-facing profile — onboarding step 9's subject matter.
 *
 * Both reads answer with the same `DoerProfileView`, and the PATCH answers with
 * the whole updated view rather than the field it wrote, so the client never
 * recomputes anything and never disagrees with what was stored.
 */
export const doerProfileService = {
  /**
   * The signed-in doer's own profile. 200 with empty fields when they have
   * never saved one — that is the editor's first state, not an error.
   */
  fetchMine: (): Promise<DoerProfileView> => api.get('/v1/doer-profiles/me').json<DoerProfileView>(),

  /**
   * Every active doer of the ACTIVE workspace, stacked.
   *
   * The workspace rides on the `x-doneverse-workspace-id` header the api client
   * injects, which is what a doer working with several founders needs.
   *
   * Null on a 404 — the founder has no workspace yet. An empty *array* is a
   * different fact (a workspace with no doer allocated), and the page says
   * something different for each, so the two must not collapse.
   */
  fetchWorkspaceDoers: async (): Promise<DoerProfileView[] | null> => {
    try {
      const { doers } = await api.get('/v1/doer-profiles').json<{ doers: DoerProfileView[] }>()
      return doers
    } catch (error: unknown) {
      if (isNotFoundError(error)) return null
      throw error
    }
  },

  /**
   * Update the doer's own profile.
   *
   * Send ONLY what changed: an omitted field is left alone and an explicit
   * `null` clears it, so echoing the whole profile back would overwrite a bio
   * edited in another tab with the copy this one happened to hold.
   */
  update: (patch: DoerProfilePatch): Promise<DoerProfileView> =>
    api.patch(patch, '/v1/doer-profiles/me').json<DoerProfileView>(),

  /**
   * Push the file to GCS and return its URL. Nothing is stored against the doer
   * until `update({ intro_video: { source: 'upload', url } })` publishes it —
   * a doer may record three takes and keep one.
   */
  uploadIntroVideo: (file: File, onProgress?: (percent: number) => void): Promise<string> =>
    uploadDoerIntroVideo(file, onProgress),
}
